'use client';
import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { AlarmClock, BookImage, CalendarClock, Copy, Download, FileClock, HardDriveDownload, MoreHorizontal, Trash2 } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import {
  Badge,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  IconButton,
} from '@freealbum/ui';
import { AlbumLoader } from '@/components/ui/album-loader';
import { LinkPendingOverlay } from '@/components/ui/link-status';
import { backupDownloadUrl } from '@/lib/api';
import { cn, daysUntil, formatDate } from '@/lib/utils';
import type { AlbumSummary } from '@freealbum/shared';

export interface AlbumCardProps {
  album: AlbumSummary;
  onDuplicate: () => void;
  onDelete: () => void;
  onExport: () => void;
  onExtendRetention: () => void;
  busy?: 'duplicating' | 'deleting' | 'extending';
}

/** Warns when originals are purged within two weeks (docs: 90-day retention, extendable). */
export const PURGE_WARNING_DAYS = 14;

export function AlbumCard({ album, onDuplicate, onDelete, onExport, onExtendRetention, busy }: AlbumCardProps) {
  const t = useTranslations('albums');
  const tb = useTranslations('backup');
  const tf = useTranslations('formats');
  const locale = useLocale();
  const days = daysUntil(album.purgeAt);
  const purgeSoon = days !== null && days < PURGE_WARNING_DAYS;

  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-sm',
        'transition-shadow duration-(--duration-base) hover:shadow-md',
        busy && 'pointer-events-none',
      )}
      aria-busy={busy ? true : undefined}
    >
      {/* Duplicating, extending or deleting: say so on the card, not only by dimming it (UX-45). */}
      {busy ? (
        <div className="absolute inset-0 z-10 grid place-items-center bg-paper/90">
          <AlbumLoader size="compact" label={t(`card.${busy}`)} />
        </div>
      ) : null}
      <Link
        href={`/albums/${album.id}/edit`}
        className="relative block aspect-4/3 overflow-hidden bg-paper-2"
        aria-label={t('card.openLabel', { title: album.title || t('card.untitled') })}
      >
        {album.coverThumbUrl ? (
          <img
            src={album.coverThumbUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-(--duration-slow) ease-out-soft group-hover:scale-[1.02]"
          />
        ) : (
          <span className="flex size-full flex-col items-center justify-center gap-2 text-ink-3">
            <BookImage aria-hidden className="size-8" />
            <span className="text-xs">{t('card.noCover')}</span>
          </span>
        )}
        {purgeSoon ? (
          <span className="absolute start-2 top-2">
            <Badge variant="warning" title={t('purge.tooltip', { date: formatDate(album.purgeAt, locale) })}>
              <AlarmClock aria-hidden className="size-3" />
              {days !== null && days <= 0 ? t('purge.badgeToday') : t('purge.badge', { days: days ?? 0 })}
            </Badge>
          </span>
        ) : null}
        <LinkPendingOverlay label={t('card.opening')} />
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-1 p-4">
        <div className="flex items-start gap-1">
          {/* An album with no title is unnamed — never the cover's status, which is a different thing. */}
          <h2 className="k-nikud min-w-0 flex-1 truncate font-display text-base text-ink">
            {album.title || t('card.untitled')}
          </h2>
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <IconButton label={t('actions.menu')} size="sm" className="-me-2 -mt-1">
                <MoreHorizontal aria-hidden className="size-4" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem icon={<Download className="size-4" />} onSelect={onExport}>
                {t('actions.export')}
              </DropdownMenuItem>
              <DropdownMenuItem asChild icon={<FileClock className="size-4" />}>
                <Link href={`/albums/${album.id}/exports`}>{t('actions.exports')}</Link>
              </DropdownMenuItem>
              {/* A plain link: the route sets Content-Disposition, so the browser saves the .fab itself. */}
              <DropdownMenuItem asChild icon={<HardDriveDownload className="size-4" />}>
                <a href={backupDownloadUrl(album.id)} download>
                  {tb('download.action')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem icon={<Copy className="size-4" />} onSelect={onDuplicate}>
                {t('actions.duplicate')}
              </DropdownMenuItem>
              <DropdownMenuItem icon={<CalendarClock className="size-4" />} onSelect={onExtendRetention}>
                {t('actions.extend')}
              </DropdownMenuItem>
              <DropdownMenuItem destructive icon={<Trash2 className="size-4" />} onSelect={onDelete}>
                {t('actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuRoot>
        </div>

        <p className="text-xs text-ink-2">{tf(album.format)}</p>
        <p className="text-xs text-ink-3">
          {t('card.pagesCount', { count: album.innerPages })} · {t('card.photosCount', { count: album.photoCount })}
        </p>
        <p className="mt-auto pt-2 text-2xs text-ink-3">
          {t('card.updated', { date: formatDate(album.updatedAt, locale) })}
          {' · '}
          {album.lastExportedAt ? t('card.lastExport', { date: formatDate(album.lastExportedAt, locale) }) : t('card.neverExported')}
        </p>
      </div>
    </article>
  );
}
